// Shopify Preictive Search - https://shopify.dev/docs/themes/navigation-search/search/predictive-search
class PredictiveSearch extends HTMLElement {
    constructor() {
        super();

        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector('#predictive-search');
        this.searchOverlay = document.querySelector('#search-overlay');
        this.resourcesType = this.input.dataset.resourcesType;
        this.mobileDisable = this.input.dataset.mobileDisable;
        this.searchUrl = this.input.dataset.searchUrl;
        this.searchUrlLocale = this.normalizeLocale(this.getLocaleFromSearchUrl(this.searchUrl));
        this.defaultSearchUrl = this.getDefaultSearchUrl(this.searchUrl);
        this.unsupportedLocales = new Set(this.getStoredUnsupportedLocales());

        if (this.mobileDisable == 'false') {
            this.input.addEventListener('input', this.debounce((event) => {
                this.onChange(event);
            }, 300).bind(this));
        }
    }

    onChange() {
        const searchTerm = this.input.value.trim();

        if (!searchTerm.length) {
            this.close();
            return;
        }

        this.getSearchResults(searchTerm, this.resourcesType);
    }

    getSearchResults(searchTerm, resourcesType) {
        const locale = this.searchUrlLocale || this.normalizeLocale(this.getLocale());
        const { urls: suggestUrls, usedPrimaryLocale } = this.getSuggestUrls(searchTerm, resourcesType, locale);

        this.fetchSuggestChain(suggestUrls, { locale, usedPrimaryLocale })
            .then((text) => {
                this.renderResults(text);
            })
            .catch((error) => {
                this.close();
                throw error;
            });
    }

    open() {
        this.predictiveSearchResults.style.display = 'block';
        this.searchOverlay.style.display = 'block';

    }

    close() {
        this.predictiveSearchResults.style.display = 'none';
        this.searchOverlay.style.display = 'none';

    }

    fetchSuggest(url) {
        return fetch(url)
            .then((response) => {
                if (!response.ok) {
                    const error = new Error(response.status);
                    error.status = response.status;
                    throw error;
                }

                return response.text();
            });
    }

    fetchSuggestChain(urls, options = {}, index = 0) {
        if (!urls || urls.length === 0) {
            const error = new Error('Missing predictive search URL');
            error.status = 0;
            return Promise.reject(error);
        }

        const url = urls[index];
        return this.fetchSuggest(url)
            .then((text) => {
                if (!this.hasPredictiveSection(text)) {
                    const error = new Error('Invalid predictive search response');
                    error.status = 417;
                    throw error;
                }

                return text;
            })
            .catch((error) => {
                if (error && error.status === 417 && index === 0 && options.usedPrimaryLocale && options.locale) {
                    this.unsupportedLocales.add(options.locale);
                    this.storeUnsupportedLocale(options.locale);
                }

                if (error && error.status === 417 && index < urls.length - 1) {
                    return this.fetchSuggestChain(urls, options, index + 1);
                }

                throw error;
            });
    }

    renderResults(text) {
        if (!text) {
            this.close();
            return;
        }

        const resultsSection = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');

        if (!resultsSection) {
            this.close();
            return;
        }

        this.predictiveSearchResults.innerHTML = resultsSection.innerHTML;
        this.open();
    }

    hasPredictiveSection(text) {
        if (!text) {
            return false;
        }

        return Boolean(
            new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('#shopify-section-predictive-search')
        );
    }

    buildSuggestUrl(searchUrl, searchTerm, resourcesType) {
        if (!searchUrl) {
            return '';
        }

        const encodedTerm = encodeURIComponent(searchTerm);
        return `${searchUrl}/suggest?q=${encodedTerm}&resources[type]=${resourcesType}&resources[limit]=10&section_id=predictive-search`;
    }

    getSuggestUrls(searchTerm, resourcesType, locale) {
        const urls = [];
        const seen = new Set();
        const usedPrimaryLocale = !locale || !this.unsupportedLocales.has(locale);

        const primaryUrl = usedPrimaryLocale ? this.buildSuggestUrl(this.searchUrl, searchTerm, resourcesType) : '';
        if (primaryUrl && usedPrimaryLocale) {
            urls.push(primaryUrl);
            seen.add(primaryUrl);
        }

        const region = this.getLocaleRegion(locale) || this.getLocaleRegion(this.searchUrlLocale);
        const fallbackLocales = [];

        if (region && locale !== `en-${region}`) {
            fallbackLocales.push(`en-${region}`);
        }

        if (locale !== 'en') {
            fallbackLocales.push('en');
        }

        fallbackLocales.push('');

        fallbackLocales.forEach((fallbackLocale) => {
            const fallbackSearchUrl = this.buildSearchUrlForLocale(fallbackLocale);
            const fallbackSuggestUrl = this.buildSuggestUrl(fallbackSearchUrl, searchTerm, resourcesType);

            if (fallbackSuggestUrl && !seen.has(fallbackSuggestUrl)) {
                urls.push(fallbackSuggestUrl);
                seen.add(fallbackSuggestUrl);
            }
        });

        return { urls, usedPrimaryLocale };
    }

    getDefaultSearchUrl(searchUrl) {
        if (!searchUrl) {
            return searchUrl;
        }

        const locale = this.normalizeLocale(this.getLocaleFromSearchUrl(searchUrl) || this.getLocale());
        const path = this.getPathFromUrl(searchUrl);
        const pathLower = path.toLowerCase();

        if (locale) {
            const localePrefix = `/${locale}`;
            if (pathLower.startsWith(`${localePrefix}/`)) {
                return path.slice(localePrefix.length) || path;
            }
        }

        const localeMatch = pathLower.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\/search(\/|$)/);
        if (localeMatch) {
            return path.slice(localeMatch[1].length + 1) || path;
        }

        return path;
    }

    getLocaleFromSearchUrl(searchUrl) {
        if (!searchUrl) {
            return '';
        }

        const path = this.getPathFromUrl(searchUrl);
        const match = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\/search(\/|$)/i);
        if (!match) {
            return '';
        }

        return match[1];
    }

    getLocale() {
        const htmlLang = (document.documentElement.getAttribute('lang') || '').trim();
        if (htmlLang) {
            return htmlLang;
        }

        if (window.Shopify && window.Shopify.locale) {
            return window.Shopify.locale;
        }

        return '';
    }

    getLocaleRegion(locale) {
        if (!locale || !locale.includes('-')) {
            return '';
        }

        return locale.split('-')[1] || '';
    }

    normalizeLocale(locale) {
        if (!locale) {
            return '';
        }

        return locale.toLowerCase().replace('_', '-');
    }

    getStoredUnsupportedLocales() {
        try {
            const raw = window.localStorage.getItem('iamota_predictive_search_unsupported_locales');
            if (!raw) {
                return [];
            }

            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return [];
        }
    }

    storeUnsupportedLocale(locale) {
        if (!locale) {
            return;
        }

        try {
            const locales = new Set(this.getStoredUnsupportedLocales());
            locales.add(locale);
            window.localStorage.setItem(
                'iamota_predictive_search_unsupported_locales',
                JSON.stringify(Array.from(locales))
            );
        } catch (error) {
            // Ignore storage failures (e.g., private mode).
        }
    }

    buildSearchUrlForLocale(locale) {
        const basePath = this.getPathFromUrl(this.defaultSearchUrl || this.searchUrl);
        if (!basePath) {
            return '';
        }

        if (!locale) {
            return basePath;
        }

        return this.applyLocaleToPath(basePath, locale);
    }

    getPathFromUrl(url) {
        if (!url) {
            return '';
        }

        const origin = window.location.origin;
        let path = url;
        if (path.startsWith(origin)) {
            path = path.slice(origin.length);
        }

        if (!path.startsWith('/')) {
            path = `/${path}`;
        }

        return path;
    }

    applyLocaleToPath(path, locale) {
        const localePattern = /^\/([a-z]{2}(?:-[a-z]{2})?)(\/|$)/i;
        const match = path.match(localePattern);

        if (match) {
            const existingLocale = match[1].toLowerCase();
            if (existingLocale === locale.toLowerCase()) {
                return path;
            }

            return path.replace(localePattern, `/${locale}$2`);
        }

        return `/${locale}${path}`;
    }

    debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }

}

customElements.define('iam-predictive-search', PredictiveSearch);
